import type { ColDef, GridApi } from "~/components/grid/AgGrid";

import { getDeepLinkFieldSpecs } from "~/helpers/aiChat/gridDeepLinkFilters";
import i18n from "~/i18n/i18n";

export interface FilterSegment {
	field: string;
	label: string;
	text: string;
}

interface TextFilterModel {
	filter?: string;
	filterTo?: string;
	filterType: "text";
	type?: string;
}

interface NumberFilterModel {
	filter?: number;
	filterTo?: number;
	filterType: "number";
	type?: string;
}

interface DateFilterModel {
	dateFrom?: string;
	dateTo?: string;
	filterType: "date";
	type?: string;
}

interface SetFilterModel {
	filterType?: "set";
	values?: (string | null)[];
}

interface MultiFilterModel {
	filterModels: (FilterEntryModel | null)[];
	filterType: "multi";
}

interface CombinedFilterModel {
	conditions: Partial<DateFilterModel | NumberFilterModel | TextFilterModel>[];
	filterType: "text" | "number" | "date";
	operator: "AND" | "OR";
}

type FilterEntryModel =
	| CombinedFilterModel
	| DateFilterModel
	| MultiFilterModel
	| NumberFilterModel
	| SetFilterModel
	| TextFilterModel;

export type FilterModelMap = Record<string, FilterEntryModel>;

type SetValueFormatter = (values: string[]) => string[];

export type SetFilterLabelResolver = (field: string, key: string) => string | undefined;

type SetKeyLabeler = (key: string) => string | undefined;

export type ColumnDisplayNameResolver = (field: string) => string | undefined;

export const resolveColumnDisplayNameFromApi = (api: GridApi | null | undefined): ColumnDisplayNameResolver => {
	return (field) => {
		const column = api?.getColumn(field);
		return column ? api?.getDisplayNameForColumn(column, "header") : undefined;
	};
};

const filterText = (key: string, values?: Record<string, unknown>): string =>
	i18n.t(`common:grid.filterText.${key}`, values);

function resolveHeaderName(field: string, columnDefs: ColDef[], getDisplayName?: ColumnDisplayNameResolver): string {
	const col = columnDefs.find((c) => c.field === field);
	if (col?.headerName) return col.headerName;
	return getDisplayName?.(field) || field;
}

function resolveSetValueFormatter(field: string, pathname: string): SetValueFormatter | undefined {
	return getDeepLinkFieldSpecs(pathname).find((spec) => spec.field === field && spec.kind === "set")?.formatValues;
}

function resolveBooleanSetFormatter(field: string, columnDefs: ColDef[]): SetValueFormatter | undefined {
	const params = columnDefs.find((c) => c.field === field)?.filterParams as
		| {
				valueFormatter?: (params: { value: unknown }) => string;
				values?: unknown;
		  }
		| undefined;
	const { valueFormatter, values } = params ?? {};
	if (!valueFormatter || !Array.isArray(values) || !values.every((value) => typeof value === "boolean"))
		return undefined;
	return (keys) => keys.map((key) => valueFormatter({ value: key === "true" }));
}

function textFilterToString(filter: TextFilterModel): string {
	const val = filter.filter ?? "";
	switch (filter.type) {
		case "contains":
			return filterText("contains", { value: val });
		case "notContains":
			return filterText("notContains", { value: val });
		case "equals":
			return filterText("equals", { value: `"${val}"` });
		case "notEqual":
			return filterText("notEqual", { value: `"${val}"` });
		case "startsWith":
			return filterText("startsWith", { value: val });
		case "endsWith":
			return filterText("endsWith", { value: val });
		case "blank":
			return filterText("blank");
		case "notBlank":
			return filterText("notBlank");
		default:
			return val ? `"${val}"` : filterText("filtered");
	}
}

function numberFilterToString(filter: NumberFilterModel): string {
	const val = filter.filter;
	switch (filter.type) {
		case "equals":
			return filterText("equals", { value: val });
		case "notEqual":
			return filterText("notEqual", { value: val });
		case "greaterThan":
			return filterText("greaterThan", { value: val });
		case "greaterThanOrEqual":
			return filterText("greaterThanOrEqual", { value: val });
		case "lessThan":
			return filterText("lessThan", { value: val });
		case "lessThanOrEqual":
			return filterText("lessThanOrEqual", { value: val });
		case "inRange":
			return filterText("between", { from: val, to: filter.filterTo });
		case "blank":
			return filterText("blank");
		case "notBlank":
			return filterText("notBlank");
		default:
			return val !== undefined ? `${val}` : filterText("filtered");
	}
}

function dateFilterToString(filter: DateFilterModel): string {
	const val = filter.dateFrom ?? "";
	switch (filter.type) {
		case "equals":
			return filterText("equals", { value: val });
		case "notEqual":
			return filterText("notEqual", { value: val });
		case "greaterThan":
			return filterText("after", { date: val });
		case "lessThan":
			return filterText("before", { date: val });
		case "greaterThanOrEqual":
			return filterText("onOrAfter", { date: val });
		case "lessThanOrEqual":
			return filterText("onOrBefore", { date: val });
		case "inRange":
			return filterText("dateRange", { from: val, to: filter.dateTo });
		case "blank":
			return filterText("blank");
		case "notBlank":
			return filterText("notBlank");
		default:
			return val || filterText("filtered");
	}
}

function setFilterToString(
	filter: SetFilterModel,
	formatValues: SetValueFormatter | undefined,
	labelForKey: SetKeyLabeler | undefined
): string {
	const rawValues = (filter.values ?? []).filter((value): value is string => typeof value === "string");
	if (rawValues.length === 0) return filterText("empty");
	const values = formatValues
		? formatValues(rawValues)
		: labelForKey
			? rawValues.map((value) => labelForKey(value) ?? value)
			: rawValues;
	if (values.length === 1) return filterText("equals", { value: values[0] });
	return filterText("anyOf", { values: values.join(" | ") });
}

function singleFilterToString(
	filter: FilterEntryModel | null,
	formatValues: SetValueFormatter | undefined,
	labelForKey: SetKeyLabeler | undefined
): string {
	if (!filter) return filterText("filtered");

	const filterType = filter.filterType;
	if (filterType === "set" || "values" in filter)
		return setFilterToString(filter as SetFilterModel, formatValues, labelForKey);
	if (filterType === "text") return textFilterToString(filter as TextFilterModel);
	if (filterType === "number") return numberFilterToString(filter as NumberFilterModel);
	if (filterType === "date") return dateFilterToString(filter as DateFilterModel);

	return filterText("filtered");
}

function filterEntryToString(
	filterEntry: FilterEntryModel | null,
	formatValues: SetValueFormatter | undefined,
	labelForKey: SetKeyLabeler | undefined
): string {
	if (!filterEntry) return filterText("filtered");

	if ("conditions" in filterEntry && Array.isArray(filterEntry.conditions)) {
		const parts = filterEntry.conditions.map((condition) =>
			filterEntryToString(
				{ ...condition, filterType: filterEntry.filterType } as FilterEntryModel,
				formatValues,
				labelForKey
			)
		);
		return parts.length > 0
			? parts.map((part) => `(${part})`).join(` ${filterText(filterEntry.operator === "OR" ? "or" : "and")} `)
			: filterText("filtered");
	}

	// Multi filter: has filterModels array
	if (filterEntry.filterType === "multi" && Array.isArray(filterEntry.filterModels)) {
		const parts = filterEntry.filterModels
			.filter((m): m is FilterEntryModel => m != null)
			.map((m) => filterEntryToString(m, formatValues, labelForKey));
		return parts.length > 0
			? (parts.length > 1 ? parts.map((part) => `(${part})`) : parts).join(` ${filterText("and")} `)
			: filterText("filtered");
	}

	return singleFilterToString(filterEntry, formatValues, labelForKey);
}

function setValueKeys(filterEntry: FilterEntryModel | null): string[] {
	if (!filterEntry) return [];
	if (filterEntry.filterType === "multi" && Array.isArray(filterEntry.filterModels))
		return filterEntry.filterModels.flatMap(setValueKeys);
	if (filterEntry.filterType === "set" || "values" in filterEntry)
		return ((filterEntry as SetFilterModel).values ?? []).filter((value): value is string => typeof value === "string");
	return [];
}

export const setFilterValueKeys = (filterModel: FilterModelMap): { field: string; key: string }[] =>
	Object.entries(filterModel).flatMap(([field, entry]) => setValueKeys(entry).map((key) => ({ field, key })));

const filterModelToText = (
	filterModel: FilterModelMap | null,
	columnDefs: ColDef[],
	pathname = "",
	labelResolver?: SetFilterLabelResolver,
	getDisplayName?: ColumnDisplayNameResolver
): FilterSegment[] => {
	if (!filterModel) return [];

	return Object.entries(filterModel).map(([field, entry]) => ({
		field,
		label: resolveHeaderName(field, columnDefs, getDisplayName),
		text: filterEntryToString(
			entry,
			resolveSetValueFormatter(field, pathname) ?? resolveBooleanSetFormatter(field, columnDefs),
			labelResolver ? (key: string) => labelResolver(field, key) : undefined
		)
	}));
};

export default filterModelToText;
